/**
 * OddsMatrixView — the PickTheOdds/OddsJam-grade odds matrix, powered by the GATE-VERIFIED core.
 * Self-contained: fetches the live married board (CF-KV plane, sole source) and renders
 * games × books with best-price highlighting from src/lib/boardGridModel.js (17/17 on the trusted gate).
 * Prices/lines VERBATIM. No receiver, no Supabase, no fallbacks — plane or fail loud.
 * Prefix: omx-
 */
import { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { buildGrid, buildGridMulti, SPREAD_BET_TYPES, isFullGame } from '../lib/boardGridModel'
import { getTeamLogo, getTeamDisplayName } from '../lib/logoMaps'
import { bookLogoSrc } from '../lib/bookLogoRegistry'
import { getLeagueLogoPath } from '../lib/leagueLogos'
import { RefreshCw } from 'lucide-react'
import './OddsMatrixView.css'

const BOARD_URL = 'https://abet-board-data.abetco.workers.dev/data/cat_game-lines.json'
const REFRESH_MS = 45000

// widget league pill id -> board league code(s); null = all
const LEAGUE_TO_BOARD = {
  ALL: null, NFL: 'NFL', NBA: 'NBA', MLB: 'MLB', NHL: 'NHL', CFB: 'CFB',
  CBB: 'CBB', NCAAW: 'NCAAW', SOCCER: 'SOCCER', MMA: 'MMA', TENNIS: 'TENNIS',
}

function fmtOdds(american, oddsFormat) {
  if (american == null) return '—'
  if (oddsFormat === 'decimal') {
    const dec = american > 0 ? 1 + american / 100 : 1 + 100 / -american
    return dec.toFixed(2)
  }
  return american > 0 ? `+${american}` : `${american}`
}

function fmtLine(line, side) {
  if (line == null) return null
  if (side === 'Over') return `O ${line}`
  if (side === 'Under') return `U ${line}`
  return line > 0 ? `+${line}` : `${line}`
}

// LOGOS: one source of truth — src/lib/bookLogoRegistry.js (gate-verified 15/15, swarm Round H).
// It resolves engine slugs against the files actually shipped, prefers svg > png > webp > ico, refuses
// debug variants (fanatics_test/_real, bovada_new/_temp), and returns null rather than guessing a
// near-match. Do NOT reintroduce getBookLogo() here — that legacy map is one of the four drifted sources
// this replaced. A missing logo renders as a text badge on purpose.
const bookLogo = (bookKey) => bookLogoSrc(bookKey)

function ageSeconds(iso) {
  const t = Date.parse(iso)
  return Number.isFinite(t) ? Math.max(0, Math.round((Date.now() - t) / 1000)) : null
}

// board league code -> widget pill id (for the "what IS available" chips)
const BOARD_TO_LEAGUE = Object.fromEntries(
  Object.entries(LEAGUE_TO_BOARD).filter(([, v]) => v !== null).map(([pill, board]) => [board, pill])
)

// Which leagues actually have lines for these bet types right now (drives an honest empty state
// instead of a dead-end "no games": NFL legitimately has 0 moneylines in July).
//
// MUST use isFullGame() from boardGridModel — the SAME predicate the grid itself uses. This
// function previously tested `r.period !== 'Game'`, which is the exact bug 7547f00 fixed in the
// grid: the engine does not emit a uniform period, so MLB rows carry "Game" while NFL/CFB carry
// null. Measured on the live board 2026-08-16: 2,289 moneyline rows, only 337 with period="Game"
// and 1,942 with period=null — the strict test discarded 1,939 of them (85%) and reported the
// chips as MLB-only, hiding every NFL/CFB/NBA/NHL league that did have lines. Exported so there
// is one definition instead of two that can drift apart again.
// Sub-periods ("1st Inning", halves/quarters) are still excluded — isFullGame rejects them.
export function availabilityByLeague(rows, betTypes) {
  const want = new Set(betTypes)
  const seen = new Map() // league -> Set(game keys)
  for (const r of rows) {
    if (r.american == null || !isFullGame(r.period) || !want.has(r.bet_type)) continue
    if (!seen.has(r.league)) seen.set(r.league, new Set())
    seen.get(r.league).add(`${r.event}|${r.et_date}`)
  }
  return Array.from(seen.entries())
    .map(([lg, games]) => ({ league: lg, games: games.size, pill: BOARD_TO_LEAGUE[lg] || null }))
    .sort((a, b) => b.games - a.games)
}

export default function OddsMatrixView({ league = 'ALL', marketFilter = 'ALL', oddsFormat = 'american', onLeagueChange = null }) {
  const [rawRows, setRawRows] = useState(null)
  const [generatedAt, setGeneratedAt] = useState(null)
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(true)
  const [tick, setTick] = useState(0) // re-render for the age stamp
  const timerRef = useRef(null)

  const load = useCallback(async () => {
    try {
      setError(null)
      const res = await fetch(`${BOARD_URL}?t=${Date.now()}`, { cache: 'no-store' })
      if (!res.ok) throw new Error(`married board HTTP ${res.status}`)
      const data = await res.json()
      if (!Array.isArray(data?.rows)) throw new Error('married board: no rows[] in payload')
      setRawRows(data.rows)
      setGeneratedAt(data.generated_utc || data.captured_utc || null)
    } catch (e) {
      setError(String(e.message || e)) // fail LOUD — no fallback source exists by design
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
    timerRef.current = setInterval(load, REFRESH_MS)
    const ageTimer = setInterval(() => setTick((t) => t + 1), 10000)
    return () => { clearInterval(timerRef.current); clearInterval(ageTimer) }
  }, [load])

  const market = marketFilter === 'ALL' ? 'ML' : marketFilter
  const boardLeague = LEAGUE_TO_BOARD[league] ?? null

  const betTypes = market === 'TOTAL' ? ['Total'] : market === 'SPREAD' ? SPREAD_BET_TYPES : ['Moneyline']

  const grid = useMemo(
    () => (rawRows ? buildGridMulti(rawRows, boardLeague, betTypes) : null),
    [rawRows, boardLeague, market] // eslint-disable-line react-hooks/exhaustive-deps
  )

  // only computed when the current selection is empty — tells the user what the board DOES have
  const availability = useMemo(
    () => (rawRows && grid && grid.games.length === 0 ? availabilityByLeague(rawRows, betTypes) : []),
    [rawRows, grid, market] // eslint-disable-line react-hooks/exhaustive-deps
  )

  if (loading) return <div className="omx-status">Loading the married board…</div>
  if (error) return (
    <div className="omx-status omx-error">
      Married board unavailable: {error}
      <button className="omx-retry" onClick={load}><RefreshCw size={14} /> Retry</button>
    </div>
  )
  if (!grid || grid.games.length === 0) {
    const marketLabel = market === 'TOTAL' ? 'totals' : market === 'SPREAD' ? 'spreads' : 'moneylines'
    const leagueLabel = boardLeague || 'any league'
    return (
      <div className="omx-status">
        <div>No <strong>{marketLabel}</strong> on the board for <strong>{leagueLabel}</strong> right now.</div>
        {availability.length > 0 && (
          <div className="omx-avail">
            <span className="omx-avail-label">On the board now:</span>
            {availability.map((a) => (
              <button
                key={a.league}
                className="omx-avail-chip"
                disabled={!a.pill || !onLeagueChange}
                onClick={() => a.pill && onLeagueChange && onLeagueChange(a.pill)}
                title={a.pill && onLeagueChange ? `Show ${a.league}` : a.league}
              >
                {a.league} <span className="omx-avail-n">{a.games}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    )
  }

  const age = generatedAt ? ageSeconds(generatedAt) : null

  // group games by league for section headers when showing ALL
  const sections = []
  {
    let cur = null
    for (const g of grid.games) {
      if (!cur || cur.league !== g.league) { cur = { league: g.league, games: [] }; sections.push(cur) }
      cur.games.push(g)
    }
  }

  return (
    <div className="omx-wrap">
      <div className="omx-meta">
        <span className="omx-src">married board · {grid.counts.books} books · {grid.counts.games} games</span>
        {age != null && <span className={`omx-age ${age > 1500 ? 'stale' : ''}`}>updated {age < 90 ? `${age}s` : `${Math.round(age / 60)}m`} ago</span>}
      </div>
      <div className="omx-scroll">
        <table className="omx-table">
          <thead>
            <tr>
              <th className="omx-game-col">Game</th>
              {grid.books.map((b) => (
                <th key={b} className="omx-book-col" title={b}>
                  {bookLogo(b)
                    ? <img className="omx-book-logo" src={bookLogo(b)} alt={b} onError={(e) => { e.target.outerHTML = `<span class=\"omx-book-abbr\">${b.slice(0, 6)}</span>` }} />
                    : <span className="omx-book-abbr">{b.slice(0, 6)}</span>}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sections.map((sec) => (
              <SectionRows key={sec.league} section={sec} books={grid.books} showHeader={boardLeague === null} oddsFormat={oddsFormat} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function SectionRows({ section, books, showHeader, oddsFormat }) {
  return (
    <>
      {showHeader && (
        <tr className="omx-league-row">
          <td colSpan={books.length + 1}>
            {getLeagueLogoPath(section.league) && <img className="omx-league-logo" src={getLeagueLogoPath(section.league)} alt="" onError={(e) => { e.target.style.display = 'none' }} />}
            <span>{section.league}</span>
            <span className="omx-league-n">{section.games.length}</span>
          </td>
        </tr>
      )}
      {section.games.map((g) => (
        <GameRows key={`${g.league}|${g.event}|${g.et_date}|${g.game_index}|${g.bet_type || ''}`} game={g} books={books} oddsFormat={oddsFormat} />
      ))}
    </>
  )
}

function GameRows({ game, books, oddsFormat }) {
  const isTotal = game.sides[0] === 'Over'
  return (
    <>
      {game.sides.map((side, i) => (
        <tr key={side} className={`omx-row ${i === game.sides.length - 1 ? 'omx-row-last' : ''}`}>
          {i === 0 && (
            <td className="omx-game-cell" rowSpan={game.sides.length}>
              <div className="omx-matchup">
                {[game.away, game.home].map((team, ti) => (
                  <div key={ti} className="omx-team">
                    {!isTotal && getTeamLogo(team, game.league) && (
                      <img className="omx-team-logo" src={getTeamLogo(team, game.league)} alt="" onError={(e) => { e.target.style.display = 'none' }} />
                    )}
                    <span>{getTeamDisplayName(team, game.league) || team}</span>
                  </div>
                ))}
                <div className="omx-when">{game.et_date?.slice(5)} · {game.et_time}{game.game_index > 0 ? ` · G${game.game_index + 1}` : ''}</div>
                {game.consensus_line != null && <div className="omx-consensus">line {game.consensus_line}</div>}
              </div>
            </td>
          )}
          {books.map((b) => {
            const cell = game.cells[b] && game.cells[b][side]
            if (!cell) return <td key={b} className="omx-cell omx-empty">—</td>
            const off = game.consensus_line != null && cell.line !== game.consensus_line
            return (
              <td key={b} className={`omx-cell ${cell.best ? 'omx-best' : ''} ${off ? 'omx-offline' : ''} ${cell.state === 'paused' ? 'omx-paused' : ''}`}>
                {cell.line != null && <span className="omx-line">{fmtLine(cell.line, side)}</span>}
                <span className="omx-price">{fmtOdds(cell.american, oddsFormat)}</span>
              </td>
            )
          })}
        </tr>
      ))}
    </>
  )
}
